import { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import authService from '../services/authService';
import { updateUser } from '../redux/slices/authSlice';
import alertify from 'alertifyjs';

/**
 * VerifyEmailPage - Email verification landing page
 * 
 * Phase 3: Email verification system
 * 
 * Features:
 * - Verify token on mount
 * - Show success/error message
 * - Redirect to dashboard after verification
 * - Resend verification email option (on error)
 * 
 * CRITICAL: Uses React Router 5!
 * Route: /verify-email/:token (public)
 */
const VerifyEmailPage = () => {
  const { token } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Geçersiz doğrulama linki');
        setLoading(false);
        return;
      }

      try {
        const response = await authService.verifyEmail(token);
        
        if (response.success) {
          setSuccess(true);
          alertify.success(response.message);
          
          // Update Redux state with verified user data
          if (response.data && response.data.user) {
            dispatch(updateUser({
              isEmailVerified: true,
              emailVerifiedAt: response.data.user.emailVerifiedAt
            }));
          }
          
          // Redirect to dashboard after 3 seconds
          setTimeout(() => {
            history.push('/dashboard');
          }, 3000);
        } else {
          setError(response.message || 'Doğrulama başarısız');
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message || 'Doğrulama başarısız. Lütfen tekrar deneyin.';
        setError(errorMessage);
        alertify.error(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token, history, dispatch]);

  // Resend verification email
  const handleResend = async () => {
    setResending(true);
    try {
      const response = await authService.resendVerificationEmail();
      
      if (response.success) {
        alertify.success(response.message);
        // Redirect to dashboard
        setTimeout(() => {
          history.push('/dashboard');
        }, 2000);
      } else {
        alertify.error(response.message);
      }
    } catch (err) {
      // Handle rate limit error (429)
      if (err.response?.status === 429) {
        const errorMessage = err.response?.data?.message || 'Çok fazla istek. Lütfen bekleyin.';
        alertify.warning(errorMessage);
      } else {
        const errorMessage = err.response?.data?.message || 'Email gönderilemedi';
        alertify.error(errorMessage);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          {/* Loading State */}
          {loading && (
            <Box>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Email Doğrulanıyor...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lütfen bekleyin
              </Typography>
            </Box>
          )}

          {/* Success State */}
          {!loading && success && (
            <Box>
              <CheckCircleIcon
                sx={{ fontSize: 80, color: 'success.main', mb: 3 }}
              />
              <Typography variant="h4" gutterBottom fontWeight="bold" color="success.main">
                Email Doğrulandı!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Email adresiniz başarıyla doğrulandı. Artık tüm özelliklere erişebilirsiniz.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Dashboard'a yönlendiriliyorsunuz...
              </Typography>
            </Box>
          )}

          {/* Error State */}
          {!loading && !success && error && (
            <Box>
              <ErrorIcon
                sx={{ fontSize: 80, color: 'error.main', mb: 3 }}
              />
              <Typography variant="h5" gutterBottom fontWeight="bold" color="error.main">
                Doğrulama Başarısız
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {error}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleResend}
                  disabled={resending}
                  fullWidth
                >
                  {resending ? 'Gönderiliyor...' : 'Yeni Doğrulama Linki Gönder'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => history.push('/dashboard')}
                  fullWidth
                >
                  Dashboard'a Dön
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default VerifyEmailPage;

